import { useCallback, useEffect, useState } from 'react';
import { useTracker } from '@/core/tracking';
import { usePageNameTracking } from '@/core/tracking/usePageNameTracking';
import { Comparer } from '@/features/comparer';
import { useResponsiveComparer } from '@/features/imageComparer/useResponsiveComparer';
import { MenuActions } from '@/layout/menuActions';
import { Navigation } from '@/layout/navigation';
import Footer from '@/layout/Footer';
import logoMobile from '@/public/images/logo-mobile.svg';
import sketchIcon from '@/public/images/icons/home-sketch.svg';
import { useLocalization } from '@/services/localizationService';
import styles from './downloadAppCta.module.css';

type Props = {
  downloadUrl: string;
};

export function DownloadAppCtaPage({ downloadUrl }: Props) {
  const { t, data, locale } = useLocalization();
  const { track } = useTracker();
  const comparerConfig = useResponsiveComparer();
  const [isFirstSlide, setIsFirstSlide] = useState(true);
  const copy = data.downloadAppCta;
  usePageNameTracking('home');

  useEffect(() => {
    track('download_app_cta', 'pageview');
  }, [track]);

  /* Same as the standard home: at most one slider interaction per session. */
  const onComparerSlide = useCallback(() => {
    if (isFirstSlide) {
      track('homepage', 'slider_interaction');
      setIsFirstSlide(false);
    }
  }, [isFirstSlide, track]);

  const onDownloadTapped = useCallback(() => {
    track('download_app_cta', 'download_app_tapped');
  }, [track]);

  return (
    <div className={styles.root}>
      <div className={styles.hero}>
        {comparerConfig.beforeSrc && comparerConfig.afterSrc && (
          <Comparer
            className="w-full h-full"
            backgroundColor={0x000}
            initialFit="cover"
            disablePan
            disablePinch
            {...comparerConfig}
            onSlide={onComparerSlide}
            sliderBehavior="hover"
          />
        )}
      </div>
      <header className={styles.header}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          {...logoMobile}
          className={styles.logo}
          alt="Remini"
          draggable={false}
        />
        <Navigation.Button className={styles.menuButton} />
      </header>
      <Navigation className="fixed z-30 inset-0 md:hidden">
        <MenuActions />
      </Navigation>
      <main className={styles.copy}>
        <p className={styles.badge} lang={locale}>
          {t(copy.badge)}
        </p>
        <h1 className={styles.title}>
          {t(copy.titleLead)}
          <span className={styles.titleAccent}>
            {t(copy.titleAccent)}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              {...sketchIcon}
              className={styles.titleSketch}
              alt=""
              draggable={false}
            />
          </span>
        </h1>
        <p className={styles.body}>{t(copy.body)}</p>
        <a className={styles.cta} href={downloadUrl} onClick={onDownloadTapped}>
          <span className={styles.ctaIcon} aria-hidden="true">
            <DownloadIcon />
          </span>
          <span className={styles.ctaCopy}>
            <strong className={styles.ctaTitle}>{t(copy.ctaTitle)}</strong>
            <small className={styles.ctaSubtitle}>{t(copy.ctaSubtitle)}</small>
          </span>
        </a>
        <p className={styles.footnote}>{t(copy.footnote)}</p>
      </main>
      <Footer className={styles.footer} />
    </div>
  );
}

function DownloadIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="m7 10 5 5 5-5" />
      <path d="M12 15V3" />
    </svg>
  );
}
